import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, message, Input, Row, Col, Tag, Modal, Form, InputNumber, DatePicker, Select, Popconfirm, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import api from '../api';
import dayjs from 'dayjs';

const { Option } = Select;

const FinancialRecords = () => {
  const location = useLocation();
  const [financialRecords, setFinancialRecords] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const BUCKET_NAME = 'financial-attachments';

  const businessTypeMap = {
    purchase: '采购',
    sales: '销售',
    adjustment: '手动调整',
  };

  const fetchFinancialRecords = useCallback(async (page, pageSize, search) => {
    setLoading(true);
    try {
      const response = await api.get('/v1/financial_records', {
        params: { page, limit: pageSize, search },
      });
      setFinancialRecords(response.data.data);
      setPagination({
        current: response.data.page,
        pageSize: response.data.limit,
        total: response.data.total,
      });
    } catch (error) {
      message.error('获取收支记录列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFinancialRecords(pagination.current, pagination.pageSize, searchTerm);
  }, [pagination.current, pagination.pageSize, searchTerm, fetchFinancialRecords]);

  useEffect(() => {
    if (location.state?.search) {
      setSearchTerm(location.state.search);
      fetchFinancialRecords(1, pagination.pageSize, location.state.search);
      // Clear the location state after applying it
      window.history.replaceState({}, document.title);
    }
  }, [location.state, fetchFinancialRecords]);

  const handleTableChange = (pagination) => {
    fetchFinancialRecords(pagination.current, pagination.pageSize, searchTerm);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    fetchFinancialRecords(1, pagination.pageSize, value);
  };

  const showModal = async (record = null, viewMode = false) => {
    setEditingRecord(record);
    setIsViewMode(viewMode);
    if (record) {
      // 如果是查看或编辑模式，先获取完整的记录数据（包含签名URL）
      try {
        const response = await api.get(`/v1/financial_records/${record.id}`);
        const fullRecord = response.data;
        form.setFieldsValue({
          ...fullRecord,
          record_date: fullRecord.record_date ? dayjs(fullRecord.record_date) : null,
        });
        if (fullRecord.attachment_urls && fullRecord.attachment_urls.length > 0) {
          const initialFiles = fullRecord.attachment_urls.map((url, index) => ({
            uid: `-${index}`,
            name: url.split('/').pop(),
            status: 'done',
            url: url,
            path: url,
          }));
          setFileList(initialFiles);
        } else {
          setFileList([]);
        }
      } catch (error) {
        message.error('获取记录详情失败');
        return;
      }
    } else {
      form.resetFields();
      form.setFieldsValue({ type: 'expense', record_date: dayjs() });
      setFileList([]);
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    setIsViewMode(false);
    setFileList([]);
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    }
    window.open(file.url || file.preview, '_blank');
  };

  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const customRequest = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucket', BUCKET_NAME);

    try {
      const response = await api.post('/v1/attachments/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      file.url = response.data.url;
      file.path = response.data.path;
      onSuccess(response.data, file);
      message.success(`${file.name} 上传成功`);
    } catch (error) {
      onError(error);
      message.error(`${file.name} 上传失败`);
    }
  };

  const handleRemove = async (file) => {
    const fileUrl = file.url;
    if (fileUrl) {
      try {
        await api.delete('/v1/attachments', { data: { url: fileUrl, bucket: BUCKET_NAME } });
        message.success(`附件删除成功`);
        const newFileList = fileList.filter(item => item.uid !== file.uid);
        setFileList(newFileList);
        return true;
      } catch (error) {
        message.error(`附件删除失败: ${error.response?.data?.error || '服务器错误'}`);
        return Promise.reject(false);
      }
    }
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    return true;
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        record_date: values.record_date.format('YYYY-MM-DD'),
        attachment_urls: fileList.map(file => file.path || file.url),
      };

      if (editingRecord) {
        await api.put(`/v1/financial_records/${editingRecord.id}`, payload);
        message.success('记录更新成功');
      } else {
        await api.post('/v1/financial_records', payload);
        message.success('记录创建成功');
      }
      setIsModalVisible(false);
      setFileList([]);
      fetchFinancialRecords(pagination.current, pagination.pageSize, searchTerm);
    } catch (error) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/v1/financial_records/${id}`);
      message.success('记录删除成功');
      fetchFinancialRecords(pagination.current, pagination.pageSize);
    } catch (error) {
        message.error(error.response?.data?.error || '删除失败');
    }
  };

  const columns = [
    {
      title: '类型', dataIndex: 'type', key: 'type',
      render: (type) => (
        <Tag color={type === 'income' ? 'green' : 'red'}>
          {type === 'income' ? '收入' : '支出'}
        </Tag>
      )
    },
    { title: '金额', dataIndex: 'amount', key: 'amount', render: (text) => `¥ ${parseFloat(text || 0).toFixed(2)}` },
    { 
      title: '业务类型', dataIndex: 'business_type', key: 'business_type',
      render: (type) => businessTypeMap[type] || type
    },
    { title: '关联单据号', dataIndex: 'reference_no', key: 'reference_no' },
    { title: '记账日期', dataIndex: 'record_date', key: 'record_date' },
    { title: '备注', dataIndex: 'note', key: 'note' },
    {
        title: '操作',
        key: 'action',
        render: (_, record) => (
            <Space size="middle">
                <a onClick={() => showModal(record, true)}>查看</a>
                {record.business_type === 'adjustment' && (
                    <>
                        <a onClick={() => showModal(record)}>编辑</a>
                        <Popconfirm
                            title="确定删除此记录吗?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="确定"
                            cancelText="取消"
                        >
                            <Button type="link" danger>删除</Button>
                        </Popconfirm>
                    </>
                )}
            </Space>
        ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
                手工记账
            </Button>
        </Col>
        <Col>
          <Input.Search
            placeholder="搜索业务类型、单据号或备注"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={handleSearch}
            style={{ width: 300 }}
            allowClear
          />
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={financialRecords}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
      />

      <Modal
        title={isViewMode ? '查看收支记录' : (editingRecord ? '编辑收支记录' : '新增收支记录')}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose
        footer={isViewMode ? [<Button key="close" onClick={handleCancel}>关闭</Button>] : [
            <Button key="back" onClick={handleCancel}>取消</Button>,
            <Button key="submit" type="primary" loading={loading} onClick={handleOk}>
              {editingRecord ? '更新' : '新增'}
            </Button>,
        ]}
      >
        <Form form={form} layout="vertical" name="financial_record_form">
          <Form.Item name="type" label="类型" rules={[{ required: true }]}>
            <Select disabled={isViewMode}>
              <Option value="income">收入</Option>
              <Option value="expense">支出</Option>
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="金额" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} precision={2} disabled={isViewMode} />
          </Form.Item>
          <Form.Item name="record_date" label="记账日期" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabled={isViewMode} />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} disabled={isViewMode} />
          </Form.Item>
          <Form.Item label="附件">
            <Upload
              customRequest={customRequest}
              fileList={fileList}
              onPreview={handlePreview}
              onChange={handleChange}
              onRemove={handleRemove}
              listType="picture-card"
              disabled={isViewMode}
            >
              {!isViewMode && fileList.length < 5 && <div><PlusOutlined /><div style={{ marginTop: 8 }}>上传</div></div>}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FinancialRecords;
